/**
 * 신청 관리 (운영자) — 모의고사·도시락 — 2026-06-22
 * - 모의고사: 신청 건 생성/목록/명단/CSV/마감/삭제
 * - 도시락: 설정(ON·제공요일·마감) + 날짜별 명단/CSV + 정기 구독자 목록
 * 백엔드: /api/v1/owner/applications/*
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { resizeImage } from '../../../utils/resizeImage';

const authHeader = (): Record<string, string> => {
  const t = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const api = (p: string) => `${API_BASE_URL}/api/v1/owner/applications${p}`;
const WD = ['일', '월', '화', '수', '목', '금', '토'];
const todayKST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
const fmtDeadline = (iso: string | null) => iso ? new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' }) : '제한 없음';
const fmtPaidAt = (iso: string) => new Date(iso).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

async function downloadCsv(url: string, fallbackName: string) {
  const res = await fetch(`${API_BASE_URL}${url}`, { headers: authHeader() });
  if (!res.ok) { alert('내보내기에 실패했습니다'); return; }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

interface EventOption { label: string; price: number; }
interface AppEvent {
  id: string; category: string; title: string; description: string | null;
  event_date: string | null; apply_deadline: string | null; capacity: number | null;
  status: string; order_count: number; options?: EventOption[];
}
interface Member { order_id?: string; name: string; student_id?: string; source?: string; applicant_role?: string; applied_at?: string; selected_option?: string | null; price?: number; menu_labels?: string[]; paid_at?: string | null; paid_by?: string | null; ordered_at?: string | null; amount?: number; }
interface NoticePreview {
  alimtalk_enabled: boolean;
  template_blocked?: boolean;   // 카카오 검수 승인 전이면 true
  template_notice?: string | null;
  sendable_count: number;
  sendable: { name: string; price: number; applicant_role: string }[];
  skipped: { name: string; reason: string }[];
  preview_text: string;
  last_sent?: { sent_at?: string; sent?: number; failed?: number; by?: string } | null;
}
const fmtWon = (n: number) => `${(n || 0).toLocaleString('ko-KR')}원`;

export default function ApplicationManagementPage() {
  const [tab, setTab] = useState<'exam' | 'meal' | 'consulting' | 'studyroom'>('exam');
  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-1">신청 관리</h1>
      <p className="text-gray-500 text-sm mb-4">모의고사·도시락·컨설팅·스터디룸 신청을 받고, 명단을 모아 발주/정산(엑셀)에 사용합니다.</p>
      <div className="flex gap-2 mb-5 flex-wrap">
        {([['exam', '📝 모의고사'], ['meal', '🍱 도시락'], ['consulting', '🧑‍🏫 컨설팅'], ['studyroom', '🪑 스터디룸']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-5 py-2 rounded-full font-semibold ${tab === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'exam' ? <ExamTab />
        : tab === 'meal' ? <MealTab />
        : tab === 'consulting' ? <ConsultingTab category="consulting" noun="컨설팅" />
        : <StudyroomRangeTab />}
    </div>
  );
}

/* ============================ 모의고사 탭 ============================ */
interface OptionInput { label: string; price: string; }
// 모의고사 기본 과목(사탐/과탐) — 새 모의고사에 기본 채움 + '빠른 입력' 버튼
const EXAM_DEFAULT_SUBJECTS = ['사탐', '과탐'];
const defaultExamOptions = (): OptionInput[] => EXAM_DEFAULT_SUBJECTS.map((s) => ({ label: s, price: '' }));
// 실시일(YYYY-MM-DD) → 신청·취소 마감 기본값 = 전날 23:59 (datetime-local 형식)
function dayBeforeDeadline(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00`);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() - 1);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T23:59`;
}

/** 과목 선택 옵션(이름만) 편집기 — 모의고사 전용. 가격은 '응시료' 하나로 통일(별도 입력) */
function OptionsEditor({ value, onChange }: { value: OptionInput[]; onChange: (v: OptionInput[]) => void }) {
  const setAt = (i: number, patch: Partial<OptionInput>) =>
    onChange(value.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const add = () => onChange([...value, { label: '', price: '' }]);
  const removeAt = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const addSubjects = () => {
    const existing = new Set(value.map((o) => o.label.trim()));
    const adds = EXAM_DEFAULT_SUBJECTS.filter((s) => !existing.has(s)).map((s) => ({ label: s, price: '' }));
    if (adds.length) onChange([...value, ...adds]);
  };
  return (
    <div className="space-y-2">
      {value.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={o.label} onChange={(e) => setAt(i, { label: e.target.value })} placeholder="과목명 (예: 사탐)" className="flex-1 border rounded-lg px-3 py-1.5 text-sm" />
          <button onClick={() => removeAt(i)} className="px-2 py-1 rounded-lg border border-red-300 text-red-500 text-xs">삭제</button>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={add} className="px-3 py-1.5 rounded-lg border border-blue-400 text-blue-600 text-sm">+ 옵션 추가</button>
        <button onClick={addSubjects} className="px-3 py-1.5 rounded-lg border border-indigo-400 text-indigo-600 text-sm">사탐·과탐 빠른 입력</button>
      </div>
    </div>
  );
}

function ExamTab() {
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');         // 학생에게 보이는 안내문(입금계좌 등)
  const [eventDate, setEventDate] = useState('');
  const [deadline, setDeadline] = useState('');
  const [options, setOptions] = useState<OptionInput[]>(defaultExamOptions());
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  // 기존 이벤트 옵션 편집
  const [editId, setEditId] = useState<string | null>(null);
  const [editOptions, setEditOptions] = useState<OptionInput[]>([]);
  const [editDesc, setEditDesc] = useState('');
  const [examFee, setExamFee] = useState('');   // 새 모의고사 응시료(공통)
  const [editFee, setEditFee] = useState('');   // 편집 중 응시료(공통)
  // 미입금자 안내 카톡 (원장이 직접 눌러 보냄)
  const [notice, setNotice] = useState<NoticePreview | null>(null);
  const [noticeBusy, setNoticeBusy] = useState(false);
  const [noticeResult, setNoticeResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await fetch(api('/events?category=mock_exam'), { headers: authHeader() });
    if (res.ok) setEvents((await res.json()).events || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // 응시료 하나(fee)를 모든 과목 옵션에 동일 적용 (사탐/과탐 등 택1, 같은 금액)
  const toPayloadOptions = (opts: OptionInput[], fee: string) =>
    opts.map((o) => ({ label: o.label.trim(), price: Number(fee) || 0 }))
        .filter((o) => o.label);

  const create = async () => {
    if (!title.trim()) { alert('제목을 입력하세요'); return; }
    setBusy(true);
    try {
      const res = await fetch(api('/events'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ category: 'mock_exam', title: title.trim(), description: desc.trim() || null, event_date: eventDate || null, apply_deadline: deadline || null, options: toPayloadOptions(options, examFee) }),
      });
      if (res.ok) { setTitle(''); setDesc(''); setEventDate(''); setDeadline(''); setOptions(defaultExamOptions()); setExamFee(''); await load(); }
      else alert('생성 실패');
    } finally { setBusy(false); }
  };
  const toggleStatus = async (e: AppEvent) => {
    await fetch(api(`/events/${e.id}`), { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ status: e.status === 'open' ? 'closed' : 'open' }) });
    await load();
  };
  const remove = async (e: AppEvent) => {
    if (!window.confirm(`"${e.title}" 신청 건을 삭제할까요? (신청 내역 포함)`)) return;
    await fetch(api(`/events/${e.id}`), { method: 'DELETE', headers: authHeader() });
    if (openId === e.id) setOpenId(null);
    await load();
  };
  const viewMembers = async (e: AppEvent) => {
    if (openId === e.id) { setOpenId(null); return; }
    const res = await fetch(api(`/events/${e.id}/orders`), { headers: authHeader() });
    if (res.ok) { setMembers((await res.json()).orders || []); setOpenId(e.id); setNotice(null); setNoticeResult(null); }
  };
  const openNotice = async (e: AppEvent) => {
    if (notice) { setNotice(null); return; }
    setNoticeResult(null);
    const res = await fetch(api(`/events/${e.id}/fee-notice`), { headers: authHeader() });
    if (!res.ok) { alert('안내 대상을 불러오지 못했습니다'); return; }
    setNotice(await res.json());
  };
  const sendNotice = async (e: AppEvent) => {
    setNoticeBusy(true);
    try {
      const res = await fetch(api(`/events/${e.id}/fee-notice/send`), { method: 'POST', headers: authHeader() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setNoticeResult(`보내지 못했습니다 — ${d.detail || '알 수 없는 오류'}`); return; }
      setNoticeResult(d.message || '보냈습니다');
      setNotice(null);
    } finally { setNoticeBusy(false); }
  };
  const setPaid = async (m: Member, paid: boolean) => {
    if (!m.order_id) return;
    const res = await fetch(api(`/orders/${m.order_id}/paid`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ paid }),
    });
    if (!res.ok) { alert(paid ? '입금 확인 실패' : '되돌리기 실패'); return; }
    const d = await res.json();
    setMembers((prev) => prev.map((x) => (x.order_id === m.order_id ? { ...x, paid_at: d.paid_at, paid_by: d.paid_by } : x)));
  };
  const startEditOptions = (e: AppEvent) => {
    if (editId === e.id) { setEditId(null); return; }
    setEditOptions((e.options || []).map((o) => ({ label: o.label, price: String(o.price) })));
    setEditFee(String((e.options?.[0]?.price) || ''));
    setEditDesc(e.description || '');
    setEditId(e.id);
  };
  const saveEditOptions = async (e: AppEvent) => {
    const res = await fetch(api(`/events/${e.id}`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ description: editDesc.trim(), options: toPayloadOptions(editOptions, editFee) }),
    });
    if (res.ok) { setEditId(null); await load(); } else alert('저장 실패');
  };

  return (
    <div>
      {/* 생성 폼 */}
      <div className="bg-white border rounded-xl p-4 mb-5 shadow-sm">
        <h3 className="font-semibold mb-3">새 모의고사 신청 만들기</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-3">
            <label className="text-xs text-gray-500">제목</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 6월 모의고사" className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs text-gray-500">안내문 <span className="text-gray-400">(선택 · 학생 신청 화면에 그대로 보입니다 · 입금계좌 등)</span></label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3}
              placeholder={'예: 응시료 입금 후 신청이 완료됩니다.\n국민 123456-78-901234 (예금주 홍길동)'}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500">실시일</label>
            <input type="date" value={eventDate} onChange={(e) => { const v = e.target.value; setEventDate(v); if (v) setDeadline(dayBeforeDeadline(v)); }} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs text-gray-500">신청·취소 마감 <span className="text-gray-400">(실시일 정하면 전날 23:59 자동 · 수정 가능)</span></label>
            <input type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs text-gray-500">응시료 (원) <span className="text-gray-400">· 과목 공통</span></label>
            <input value={examFee} onChange={(e) => setExamFee(e.target.value.replace(/[^0-9]/g, ''))} placeholder="예: 12000" inputMode="numeric" className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs text-gray-500 block mb-1">과목 선택 <span className="text-gray-400">(기본으로 사탐·과탐 채워짐 · 학생이 하나만 고름 · 금액은 위 응시료로 공통 · 불필요하면 삭제)</span></label>
            <OptionsEditor value={options} onChange={setOptions} />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button onClick={create} disabled={busy} className="bg-blue-600 text-white rounded-lg px-6 py-2 font-semibold">신청 건 만들기</button>
          </div>
        </div>
      </div>

      {/* 목록 */}
      {events.length === 0 ? (
        <div className="text-gray-400 text-center py-10">아직 신청 건이 없습니다</div>
      ) : events.map((e) => (
        <div key={e.id} className="bg-white border rounded-xl p-4 mb-3 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-bold text-lg">
                {e.title}
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${e.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                  {e.status === 'open' ? '신청중' : '마감'}
                </span>
              </div>
              <div className="text-sm text-gray-500 mt-1">
                실시 {e.event_date || '미정'} · 마감 {fmtDeadline(e.apply_deadline)} · <b className="text-blue-600">신청 {e.order_count}명</b>
              </div>
              {(e.options && e.options.length > 0) && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {e.options.map((o, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200">{o.label} · {fmtWon(o.price)}</span>
                  ))}
                </div>
              )}
              {e.description && (
                <div className="mt-1.5 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 whitespace-pre-wrap">
                  <span className="text-amber-700 font-semibold">안내문</span> · 학생에게 보임<br />{e.description}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => viewMembers(e)} className="px-3 py-1.5 rounded-lg border text-sm">{openId === e.id ? '명단 닫기' : '명단 보기'}</button>
              <button onClick={() => startEditOptions(e)} className="px-3 py-1.5 rounded-lg border text-sm">{editId === e.id ? '편집 닫기' : '안내문·옵션 편집'}</button>
              <button onClick={() => downloadCsv(`/api/v1/owner/applications/events/${e.id}/export`, `${e.title}_명단.csv`)} className="px-3 py-1.5 rounded-lg border border-green-500 text-green-600 text-sm">엑셀(CSV)</button>
              <button onClick={() => toggleStatus(e)} className="px-3 py-1.5 rounded-lg border text-sm">{e.status === 'open' ? '마감하기' : '다시 열기'}</button>
              <button onClick={() => remove(e)} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-500 text-sm">삭제</button>
            </div>
          </div>
          {editId === e.id && (
            <div className="mt-3 border-t pt-3">
              <div className="mb-3">
                <label className="text-xs text-gray-500 block mb-1">안내문 <span className="text-gray-400">(학생 신청 화면에 그대로 보입니다 · 입금계좌 등 · 비우고 저장하면 삭제)</span></label>
                <textarea value={editDesc} onChange={(ev) => setEditDesc(ev.target.value)} rows={3}
                  placeholder={'예: 응시료 입금 후 신청이 완료됩니다.\n국민 123456-78-901234 (예금주 홍길동)'}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="mb-2">
                <label className="text-xs text-gray-500 mr-2">응시료 (원) · 과목 공통</label>
                <input value={editFee} onChange={(ev) => setEditFee(ev.target.value.replace(/[^0-9]/g, ''))} placeholder="예: 12000" inputMode="numeric" className="w-32 border rounded-lg px-3 py-1.5 text-sm" />
              </div>
              <div className="text-xs text-gray-500 mb-2">과목 선택 (예: 사탐 / 과탐). 비우고 저장하면 과목 선택 없이 신청받습니다.</div>
              <OptionsEditor value={editOptions} onChange={setEditOptions} />
              <button onClick={() => saveEditOptions(e)} className="mt-2 bg-blue-600 text-white rounded-lg px-4 py-1.5 text-sm font-semibold">저장</button>
            </div>
          )}
          {openId === e.id && (
            <div className="mt-3 border-t pt-3">
              {members.length === 0 ? <div className="text-gray-400 text-sm">신청자가 없습니다</div> : (
                <>
                  <div className="flex items-center gap-3 mb-2 text-sm flex-wrap">
                    <span className="px-2.5 py-1 rounded-lg bg-green-50 text-green-700 border border-green-200 font-semibold">
                      입금 {members.filter((m) => m.paid_at).length}명
                      <span className="font-normal text-green-600"> · {fmtWon(members.filter((m) => m.paid_at).reduce((s, m) => s + (m.price || 0), 0))}</span>
                    </span>
                    <span className="px-2.5 py-1 rounded-lg bg-red-50 text-red-600 border border-red-200 font-semibold">
                      미입금 {members.filter((m) => !m.paid_at).length}명
                      <span className="font-normal"> · {fmtWon(members.filter((m) => !m.paid_at).reduce((s, m) => s + (m.price || 0), 0))}</span>
                    </span>
                    <span className="text-gray-400 text-xs">통장을 보고 직접 표시합니다 (은행 자동 대조 아님)</span>
                    <span className="flex-1" />
                    {members.some((m) => !m.paid_at) && (
                      <button onClick={() => openNotice(e)} className="px-3 py-1 rounded-lg border border-amber-400 text-amber-700 bg-amber-50 text-xs font-semibold">
                        {notice ? '닫기' : '💬 미입금자에게 카톡 보내기'}
                      </button>
                    )}
                  </div>
                  {noticeResult && (
                    <div className="mb-2 text-sm px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-800">{noticeResult}</div>
                  )}
                  {notice && (
                    <div className="mb-3 border border-amber-300 bg-amber-50/60 rounded-lg p-3">
                      {!notice.alimtalk_enabled ? (
                        <div className="text-sm text-red-600">
                          이 매장은 카카오 알림톡이 꺼져 있어 보낼 수 없습니다. [설정] → [알림톡]에서 켠 뒤 다시 시도해 주세요.
                        </div>
                      ) : notice.template_blocked ? (
                        <div className="text-sm text-amber-800">
                          <div className="font-semibold mb-1">⏳ 아직 보낼 수 없습니다</div>
                          <div className="text-xs leading-relaxed">{notice.template_notice}</div>
                        </div>
                      ) : notice.sendable_count === 0 ? (
                        <div className="text-sm text-gray-600">보낼 대상이 없습니다. (미입금자가 없거나 연락처가 없습니다)</div>
                      ) : (
                        <>
                          <div className="text-sm font-semibold mb-1">
                            미입금 {notice.sendable_count}명에게 보냅니다
                            <span className="font-normal text-gray-500"> · 신청한 분(학부모가 신청했으면 학부모)에게 갑니다</span>
                          </div>
                          <div className="text-xs text-gray-600 mb-2">
                            {notice.sendable.map((s) => `${s.name}(${s.applicant_role === 'parent' ? '학부모' : '학생'})`).join(', ')}
                          </div>
                          {notice.skipped.length > 0 && (
                            <div className="text-xs text-red-500 mb-2">
                              못 보내는 사람 {notice.skipped.length}명 — {notice.skipped.map((s) => `${s.name}(${s.reason})`).join(', ')}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mb-1">보낼 내용</div>
                          <pre className="text-xs bg-white border rounded-lg p-2 whitespace-pre-wrap mb-2">{notice.preview_text}</pre>
                          {notice.last_sent?.sent_at && (
                            <div className="text-xs text-gray-500 mb-2">
                              마지막 발송 {fmtPaidAt(notice.last_sent.sent_at)} · {notice.last_sent.sent}명
                              {notice.last_sent.by ? ` (${notice.last_sent.by})` : ''}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button onClick={() => sendNotice(e)} disabled={noticeBusy}
                              className="px-4 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-semibold disabled:opacity-50">
                              {noticeBusy ? '보내는 중…' : `${notice.sendable_count}명에게 지금 보내기`}
                            </button>
                            <button onClick={() => setNotice(null)} className="px-3 py-1.5 rounded-lg border text-sm text-gray-500">취소</button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="divide-y border rounded-lg">
                    {members.map((m, i) => (
                      <div key={m.order_id || i} className={`flex items-center gap-2 px-3 py-2 text-sm ${m.paid_at ? 'bg-green-50/50' : ''}`}>
                        <span className="text-gray-400 w-6 shrink-0">{i + 1}</span>
                        <span className="font-semibold">{m.name}</span>
                        {m.selected_option && <span className="text-indigo-600 text-xs">[{m.selected_option}]</span>}
                        {!!m.price && <span className="text-gray-600 text-xs">{fmtWon(m.price)}</span>}
                        <span className="text-gray-400 text-xs">({m.applicant_role === 'parent' ? '학부모' : '학생'})</span>
                        <span className="flex-1" />
                        {m.paid_at ? (
                          <>
                            <span className="text-green-700 text-xs font-semibold" title={m.paid_by ? `확인: ${m.paid_by}` : undefined}>
                              ✓ 입금 {fmtPaidAt(m.paid_at)}
                            </span>
                            <button onClick={() => setPaid(m, false)} className="px-2 py-1 rounded-lg border text-xs text-gray-500">되돌리기</button>
                          </>
                        ) : (
                          <button onClick={() => setPaid(m, true)} className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-semibold">입금 확인</button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ============================ 도시락 탭 ============================ */
type MealCat = 'dosirak' | 'salad';
type MealSlot = 'lunch' | 'dinner';
interface MenuType { key: string; label: string; price: number; image_id: string | null; image_url: string | null; category: MealCat; slot: MealSlot; }
interface SlotCfg { enabled: boolean; offered_weekdays: number[]; cutoff_time: string; cutoff_day_offset: number; }
interface MealSettings {
  is_enabled: boolean;
  slots: Record<MealSlot, SlotCfg>;
  menu_types: MenuType[]; monthly_menu_image_id: string | null; monthly_menu_image_url: string | null;
}
interface DaySlot {
  label: string; enabled: boolean; count: number; locked: boolean; deadline: string; members: Member[];
  // 입금 집계는 **일별 신청분만** 센다. 정기 구독은 그 날짜 신청 행이 없어 입금 대상이 아니다.
  paid_count?: number; unpaid_count?: number; paid_amount?: number; unpaid_amount?: number;
  subscription_count?: number;
}

// 도시락 시간대 슬롯(점심/저녁) + 종류 카테고리(도시락/샐러드, 슬롯별 각 최대 3종) — 2026-07-05
const MEAL_SLOTS: { slot: MealSlot; label: string }[] = [
  { slot: 'lunch', label: '점심' },
  { slot: 'dinner', label: '저녁' },
];
const MEAL_CATS: { cat: MealCat; label: string }[] = [
  { cat: 'dosirak', label: '도시락' },
  { cat: 'salad', label: '샐러드' },
];
const MEAL_MAX_PER_CAT = 3;
const DEFAULT_SLOT_CFG: SlotCfg = { enabled: false, offered_weekdays: [1, 2, 3, 4, 5], cutoff_time: '09:00', cutoff_day_offset: 0 };
let _menuKeySeq = 0;
const newMenuKey = (slot: string, cat: string) => `${slot}_${cat}_${Date.now()}_${_menuKeySeq++}`;

/** 축소 후 업로드 → image_id 반환 */
async function uploadMealImage(file: File): Promise<string | null> {
  try {
    const blob = await resizeImage(file);
    const fd = new FormData();
    fd.append('file', blob, 'meal.jpg');
    const res = await fetch(api('/meal/image'), { method: 'POST', headers: authHeader(), body: fd });
    if (!res.ok) { alert((await res.json().catch(() => ({}))).detail || '이미지 업로드 실패'); return null; }
    return (await res.json()).image_id as string;
  } catch (err) {
    alert('이미지 처리에 실패했습니다'); return null;
  }
}
const imgUrl = (url: string | null) => (url ? `${API_BASE_URL}${url}` : null);

function MealTab() {
  const [settings, setSettings] = useState<MealSettings | null>(null);
  const [date, setDate] = useState(todayKST());
  const [day, setDay] = useState<{ slots: Record<MealSlot, DaySlot> } | null>(null);
  const [busy, setBusy] = useState(false);
  // 기간(월 정산) 엑셀용 — 기본: 이번 달 1일 ~ 오늘
  const monthStart = todayKST().slice(0, 8) + '01';
  const [rangeStart, setRangeStart] = useState(monthStart);
  const [rangeEnd, setRangeEnd] = useState(todayKST());

  const loadSettings = useCallback(async () => {
    const res = await fetch(api('/meal/settings'), { headers: authHeader() });
    if (res.ok) {
      const s = (await res.json()).settings as any;
      const menus: MenuType[] = (s.menu_types || []).map((m: any) => ({
        key: m.key, label: m.label, price: m.price || 0,
        image_id: m.image_id ?? null, image_url: m.image_url ?? null,
        category: (m.category === 'salad' ? 'salad' : 'dosirak') as MealCat,
        slot: (m.slot === 'dinner' ? 'dinner' : 'lunch') as MealSlot,
      }));
      const slots: Record<MealSlot, SlotCfg> = {
        lunch: { ...DEFAULT_SLOT_CFG, enabled: true, ...(s.slots?.lunch || {}) },
        dinner: { ...DEFAULT_SLOT_CFG, cutoff_time: '17:00', ...(s.slots?.dinner || {}) },
      };
      setSettings({
        is_enabled: !!s.is_enabled, slots, menu_types: menus,
        monthly_menu_image_id: s.monthly_menu_image_id ?? null,
        monthly_menu_image_url: s.monthly_menu_image_url ?? null,
      });
    }
  }, []);
  const loadDay = useCallback(async (d: string) => {
    const res = await fetch(api(`/meal/day?date=${d}`), { headers: authHeader() });
    if (res.ok) setDay(await res.json());
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadDay(date); }, [date, loadDay]);

  // 입금 확인 / 되돌리기 — 모의고사와 **같은 API**(PATCH /orders/{id}/paid)를 그대로 쓴다.
  // 원장이 통장을 보고 직접 누르는 것이지 은행 자동대조가 아니다.
  const setMealPaid = async (m: Member, paid: boolean) => {
    if (!m.order_id) return;   // 정기 구독은 그 날짜 신청 행이 없어 대상이 아니다
    setBusy(true);
    try {
      const res = await fetch(api(`/orders/${m.order_id}/paid`), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ paid }),
      });
      if (!res.ok) { alert(paid ? '입금 확인에 실패했습니다' : '되돌리기에 실패했습니다'); return; }
      await loadDay(date);   // 합계까지 다시 받아야 위 요약이 맞는다
    } finally { setBusy(false); }
  };

  const cancelMeal = async (slot: MealSlot, label: string, m: Member) => {
    if (!m.student_id) { alert('학생 정보를 찾을 수 없어 취소할 수 없습니다'); return; }
    if (!window.confirm(`${m.name} 님의 ${label} 도시락 신청을 취소하시겠습니까?\n(취소분은 이달 정산 금액에서 제외됩니다)`)) return;
    setBusy(true);
    try {
      const res = await fetch(api('/meal/day/cancel'), {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, slot, student_id: m.student_id, student_name: m.name }),
      });
      if (!res.ok) { alert((await res.json().catch(() => ({}))).detail || '취소에 실패했습니다'); return; }
      await loadDay(date);
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      const payload = {
        is_enabled: settings.is_enabled,
        slots: settings.slots,
        // 종류: 이름 있고(사진 등록됐거나 가격>0)인 것만 전송(빈 항목 저장 방지)
        menu_types: settings.menu_types
          .filter((m) => (m.label || '').trim() && (m.image_id || m.price > 0))
          .map((m) => ({ key: m.key, label: m.label.trim(), price: m.price || 0, image_id: m.image_id, category: m.category, slot: m.slot })),
        monthly_menu_image_id: settings.monthly_menu_image_id ?? '',
      };
      const res = await fetch(api('/meal/settings'), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(payload) });
      if (res.ok) { alert('도시락 설정이 저장되었습니다'); await loadSettings(); await loadDay(date); }
      else { const e = await res.json().catch(() => ({})); alert(e.detail || '저장 실패'); }
    } finally { setBusy(false); }
  };
  const setSlotCfg = (slot: MealSlot, patch: Partial<SlotCfg>) => {
    if (!settings) return;
    setSettings({ ...settings, slots: { ...settings.slots, [slot]: { ...settings.slots[slot], ...patch } } });
  };
  const toggleWeekday = (slot: MealSlot, w: number) => {
    if (!settings) return;
    const wd = settings.slots[slot].offered_weekdays;
    setSlotCfg(slot, { offered_weekdays: wd.includes(w) ? wd.filter((x) => x !== w) : [...wd, w].sort() });
  };
  const setMenuAt = (key: string, patch: Partial<MenuType>) => {
    if (!settings) return;
    setSettings({ ...settings, menu_types: settings.menu_types.map((m) => (m.key === key ? { ...m, ...patch } : m)) });
  };
  const addMenu = (slot: MealSlot, cat: MealCat) => {
    if (!settings) return;
    const cnt = settings.menu_types.filter((m) => m.slot === slot && m.category === cat).length;
    if (cnt >= MEAL_MAX_PER_CAT) { alert(`${cat === 'dosirak' ? '도시락' : '샐러드'}는 최대 ${MEAL_MAX_PER_CAT}개까지 추가할 수 있습니다`); return; }
    setSettings({ ...settings, menu_types: [...settings.menu_types, { key: newMenuKey(slot, cat), label: '', price: 0, image_id: null, image_url: null, category: cat, slot }] });
  };
  const removeMenu = (key: string) => {
    if (!settings) return;
    setSettings({ ...settings, menu_types: settings.menu_types.filter((m) => m.key !== key) });
  };
  const onPickMenuImage = async (key: string, file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const id = await uploadMealImage(file);
      if (id) setMenuAt(key, { image_id: id, image_url: `/api/v1/applications/meal/image/${id}` });
    } finally { setBusy(false); }
  };
  const onPickMonthlyImage = async (file: File | undefined) => {
    if (!file || !settings) return;
    setBusy(true);
    try {
      const id = await uploadMealImage(file);
      if (id) setSettings({ ...settings, monthly_menu_image_id: id, monthly_menu_image_url: `/api/v1/applications/meal/image/${id}` });
    } finally { setBusy(false); }
  };

  if (!settings) return <div className="text-gray-400 py-10 text-center">불러오는 중...</div>;

  // 한 시간대(점심/저녁) 설정 패널 — 운영요일·마감·메뉴(도시락/샐러드 각 최대3)
  const renderSlotPanel = (slot: MealSlot, slotLabel: string) => {
    const cfg = settings.slots[slot];
    return (
      <div key={slot} className="border rounded-xl p-3 mb-3">
        <label className="flex items-center gap-2 text-sm font-bold mb-3">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => setSlotCfg(slot, { enabled: e.target.checked })} />
          🍱 {slotLabel} 받기
        </label>
        {cfg.enabled && (
          <>
            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-1">{slotLabel} 제공 요일</label>
              <div className="flex gap-1.5">
                {WD.map((w, i) => (
                  <button key={i} onClick={() => toggleWeekday(slot, i)}
                    className={`w-9 h-9 rounded-full text-sm font-semibold ${cfg.offered_weekdays.includes(i) ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-500'}`}>{w}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div>
                <label className="text-xs text-gray-500">{slotLabel} 신청·취소 마감 시각</label>
                <input type="time" value={cfg.cutoff_time} onChange={(e) => setSlotCfg(slot, { cutoff_time: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="text-xs text-gray-500">마감 기준</label>
                <select value={cfg.cutoff_day_offset} onChange={(e) => setSlotCfg(slot, { cutoff_day_offset: Number(e.target.value) })} className="w-full border rounded-lg px-3 py-2">
                  <option value={0}>당일</option>
                  <option value={1}>전날</option>
                </select>
              </div>
            </div>
            <div className="mt-4 border-t pt-4">
              <label className="text-xs text-gray-500 block mb-2">{slotLabel} 메뉴 (사진 + 가격) — 학생이 사진 보고 선택 (여러 개 가능)</label>
              {MEAL_CATS.map((c) => {
                const items = settings.menu_types.filter((m) => m.slot === slot && m.category === c.cat);
                return (
                  <div key={c.cat} className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        {c.label} <span className="text-xs text-gray-400">({items.length}/{MEAL_MAX_PER_CAT})</span>
                      </span>
                      <button onClick={() => addMenu(slot, c.cat)} disabled={items.length >= MEAL_MAX_PER_CAT}
                        className="px-3 py-1 rounded-lg border border-blue-400 text-blue-600 text-xs disabled:opacity-40 disabled:cursor-not-allowed">
                        + {c.label} 추가
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {items.map((m) => (
                        <div key={m.key} className="border rounded-xl p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <input value={m.label} onChange={(e) => setMenuAt(m.key, { label: e.target.value })}
                              placeholder={`${c.label} 이름`} className="flex-1 border rounded-lg px-2 py-1 text-sm font-semibold" />
                            <button onClick={() => removeMenu(m.key)}
                              className="px-2 py-1 rounded-lg border border-red-300 text-red-500 text-xs shrink-0">삭제</button>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="w-24 h-24 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                              {m.image_url
                                ? <img src={imgUrl(m.image_url) || ''} alt={m.label} className="w-full h-full object-cover" />
                                : <span className="text-gray-400 text-xs text-center px-1">사진 없음</span>}
                            </div>
                            <div className="flex-1 space-y-2">
                              <label className="block">
                                <span className="text-xs text-blue-600 cursor-pointer underline">사진 {m.image_url ? '변경' : '등록'}</span>
                                <input type="file" accept="image/*" className="hidden"
                                  onChange={(e) => onPickMenuImage(m.key, e.target.files?.[0])} />
                              </label>
                              <div className="flex items-center gap-1">
                                <input value={m.price ? String(m.price) : ''} inputMode="numeric"
                                  onChange={(e) => setMenuAt(m.key, { price: Number(e.target.value.replace(/[^0-9]/g, '')) || 0 })}
                                  placeholder="가격" className="w-24 border rounded-lg px-2 py-1 text-sm" />
                                <span className="text-xs text-gray-400">원</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {items.length === 0 && <div className="text-xs text-gray-400 py-2 col-span-full">등록된 {slotLabel} {c.label} 종류가 없습니다.</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  const anySlotOn = MEAL_SLOTS.some(({ slot }) => day?.slots?.[slot]?.enabled);

  return (
    <div>
      {/* 설정 */}
      <div className="bg-white border rounded-xl p-4 mb-5 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">도시락 운영 설정</h3>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={settings.is_enabled} onChange={(e) => setSettings({ ...settings, is_enabled: e.target.checked })} />
            도시락 신청 받기 (전체)
          </label>
        </div>
        <p className="text-xs text-gray-400 mb-4">점심·저녁을 각각 켜고 운영요일·마감시간·메뉴를 따로 설정하세요. 학생은 점심/저녁을 독립적으로 신청합니다.</p>
        {MEAL_SLOTS.map(({ slot, label }) => renderSlotPanel(slot, label))}

        {/* 월 식단표 이미지 (점심/저녁 공통) */}
        <div className="mt-2 border-t pt-4">
          <label className="text-xs text-gray-500 block mb-2">이번 달 식단표 이미지 (1장, 점심·저녁 공통)</label>
          <div className="flex items-start gap-3">
            <div className="w-32 h-32 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {settings.monthly_menu_image_url
                ? <img src={imgUrl(settings.monthly_menu_image_url) || ''} alt="월 식단표" className="w-full h-full object-cover" />
                : <span className="text-gray-400 text-xs text-center px-1">식단표 없음</span>}
            </div>
            <div className="space-y-2">
              <label className="block">
                <span className="text-xs text-blue-600 cursor-pointer underline">식단표 {settings.monthly_menu_image_url ? '변경' : '등록'}</span>
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => onPickMonthlyImage(e.target.files?.[0])} />
              </label>
              {settings.monthly_menu_image_url && (
                <button onClick={() => setSettings({ ...settings, monthly_menu_image_id: null, monthly_menu_image_url: null })}
                  className="block px-2 py-1 rounded-lg border border-red-300 text-red-500 text-xs">식단표 제거</button>
              )}
            </div>
          </div>
        </div>

        <button onClick={saveSettings} disabled={busy} className="mt-4 bg-blue-600 text-white rounded-lg px-4 py-2 font-semibold">설정 저장</button>
      </div>

      {/* 기간(월 정산) 명단 엑셀 */}
      <div className="bg-white border rounded-xl p-4 mb-5 shadow-sm">
        <h3 className="font-semibold mb-1">기간별 명단 엑셀 (월 정산용)</h3>
        <p className="text-xs text-gray-400 mb-3">시작일~종료일을 골라 그 기간의 도시락 신청을 한 파일로 받습니다. (먹는 날·요일·점심/저녁·이름·메뉴·금액·<b>신청시각</b>·<b>입금여부</b> 포함)</p>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="border rounded-lg px-3 py-1.5" />
          <span className="text-gray-400">~</span>
          <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="border rounded-lg px-3 py-1.5" />
          <button
            onClick={() => {
              if (rangeEnd < rangeStart) { alert('종료일이 시작일보다 빠릅니다'); return; }
              downloadCsv(`/api/v1/owner/applications/meal/range/export?start=${rangeStart}&end=${rangeEnd}`, `도시락_${rangeStart}~${rangeEnd}_명단.csv`);
            }}
            className="px-4 py-1.5 rounded-lg bg-green-600 text-white text-sm font-semibold">기간 엑셀 받기</button>
        </div>
      </div>

      {/* 날짜별 명단 (점심/저녁) */}
      <div className="bg-white border rounded-xl p-4 mb-5 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-semibold">날짜별 신청 명단 (발주용)</h3>
          <div className="flex items-center gap-2">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border rounded-lg px-3 py-1.5" />
            <button onClick={() => downloadCsv(`/api/v1/owner/applications/meal/day/export?date=${date}`, `도시락_${date}_명단.csv`)} className="px-3 py-1.5 rounded-lg border border-green-500 text-green-600 text-sm">엑셀(CSV)</button>
          </div>
        </div>
        {day && MEAL_SLOTS.map(({ slot, label }) => {
          const ds = day.slots?.[slot];
          if (!ds || !ds.enabled) return null;
          return (
            <div key={slot} className="mb-4 last:mb-0">
              <div className="text-sm text-gray-600 mb-2 font-semibold">
                {label} — <b className="text-blue-600">{ds.count}명</b>
                <span className="text-gray-400 font-normal"> · 마감 {fmtDeadline(ds.deadline)}</span>
                {ds.locked && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">마감됨(확정)</span>}
              </div>
              {/* 입금 요약 — 정기 구독은 일별 입금 대상이 아니라 따로 센다 */}
              {ds.members.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">
                    입금 {ds.paid_count || 0}명 · {fmtWon(ds.paid_amount || 0)}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${(ds.unpaid_count || 0) > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400'}`}>
                    미입금 {ds.unpaid_count || 0}명 · {fmtWon(ds.unpaid_amount || 0)}
                  </span>
                  {!!ds.subscription_count && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                          title="매주 요일을 정해 드시는 정기 구독입니다. 하루치 입금 확인 대상이 아니라 월 단위로 정산합니다.">
                      정기 {ds.subscription_count}명 (월정산)
                    </span>
                  )}
                </div>
              )}
              {ds.members.length === 0 ? <div className="text-gray-400 text-sm">신청자가 없습니다</div> : (
                <div className="divide-y border rounded-lg">
                  {ds.members.map((m, i) => (
                    <div key={m.order_id || m.student_id || i}
                         className={`flex items-center gap-2 px-3 py-2 text-sm ${m.paid_at ? 'bg-green-50/50' : ''}`}>
                      <span className="text-gray-400 w-6 shrink-0">{i + 1}</span>
                      <span className="font-semibold">{m.name}</span>
                      {m.menu_labels && m.menu_labels.length > 0 && (
                        <span className="text-amber-700 text-xs">[{m.menu_labels.join(', ')}]</span>
                      )}
                      {!!m.amount && <span className="text-gray-600 text-xs">{fmtWon(m.amount)}</span>}
                      <span className="text-gray-400 text-xs">({m.source})</span>
                      {m.ordered_at && (
                        <span className="text-gray-400 text-xs hidden sm:inline" title="신청한 때">
                          {fmtPaidAt(m.ordered_at)} 신청
                        </span>
                      )}
                      <span className="flex-1" />
                      {!m.order_id ? (
                        <span className="text-amber-600 text-xs" title="정기 구독은 하루치 입금 확인 대상이 아닙니다. 월 단위로 정산하세요.">
                          정기(월정산)
                        </span>
                      ) : m.paid_at ? (
                        <>
                          <span className="text-green-700 text-xs font-semibold" title={m.paid_by ? `확인: ${m.paid_by}` : undefined}>
                            ✓ 입금 {fmtPaidAt(m.paid_at)}
                          </span>
                          <button onClick={() => setMealPaid(m, false)} disabled={busy}
                                  className="px-2 py-1 rounded-lg border text-xs text-gray-500 disabled:opacity-40">되돌리기</button>
                        </>
                      ) : (
                        <button onClick={() => setMealPaid(m, true)} disabled={busy}
                                className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-semibold disabled:opacity-40">입금 확인</button>
                      )}
                      <button
                        type="button"
                        onClick={() => cancelMeal(slot, label, m)}
                        disabled={busy}
                        title="신청 취소"
                        className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200 text-xs leading-none disabled:opacity-40"
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {day && !anySlotOn && <div className="text-gray-400 text-sm">운영 중인 시간대가 없습니다 (설정에서 점심/저녁을 켜세요).</div>}
      </div>
    </div>
  );
}

/* ============================ 컨설팅 탭 ============================ */
const capi = (p: string) => `${API_BASE_URL}/api/v1/owner/consulting${p}`;

interface Program {
  id: string; title: string; description: string | null; status: string; slot_count: number; booking_count: number;
  // 스터디룸 범위예약(range) 설정
  booking_mode?: string;
  open_time?: string | null; close_time?: string | null; slot_minutes?: number;
  duration_options?: number[]; capacity?: number; open_weekdays?: number[]; advance_days?: number;
}
interface Slot {
  id: string; program_id: string; slot_date: string; weekday: number; weekday_label: string;
  start_time: string; end_time: string; capacity: number; booked_count: number;
  remaining: number | null; status: string; is_full: boolean; is_past: boolean;
}
interface Booking { student_id: string; name: string; applied_at: string | null; }

function ConsultingTab({ category, noun }: { category: 'consulting' | 'studyroom'; noun: string }) {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const example = category === 'studyroom' ? '스터디룸1, 세미나실, 1인 부스' : '입시 컨설팅, 명상';

  // 스터디룸 주당 신청 제한 (스터디룸 탭 전용)
  const [weeklyLimit, setWeeklyLimit] = useState<number>(1);
  const [savingLimit, setSavingLimit] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(capi(`/programs?category=${category}`), { headers: authHeader() });
    if (res.ok) setPrograms((await res.json()).programs || []);
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const loadSettings = useCallback(async () => {
    if (category !== 'studyroom') return;
    const res = await fetch(capi('/studyroom-settings'), { headers: authHeader() });
    if (res.ok) { const d = await res.json(); setWeeklyLimit(d.weekly_limit ?? 1); }
  }, [category]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveLimit = async () => {
    setSavingLimit(true);
    try {
      const res = await fetch(capi('/studyroom-settings'), {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ weekly_limit: weeklyLimit }),
      });
      if (res.ok) alert('저장되었습니다'); else alert((await res.json().catch(() => ({}))).detail || '저장 실패');
    } finally { setSavingLimit(false); }
  };

  const create = async () => {
    if (!title.trim()) { alert(`${noun} 이름을 입력하세요`); return; }
    setBusy(true);
    try {
      const res = await fetch(capi('/programs'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ title: title.trim(), description: desc.trim() || null, category }),
      });
      if (!res.ok) { alert((await res.json().catch(() => ({}))).detail || '생성 실패'); return; }
      setTitle(''); setDesc(''); await load();
    } finally { setBusy(false); }
  };

  return (
    <div>
      {category === 'studyroom' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
          <h3 className="font-semibold mb-1">🪑 스터디룸 신청 제한</h3>
          <p className="text-xs text-gray-500 mb-3">학생이 스터디룸을 주당 몇 번까지 신청할 수 있는지 설정합니다. (모든 스터디룸 방 합산 · 월~일 기준 · 0 = 무제한)</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">주당</span>
            <input type="number" min={0} value={weeklyLimit}
              onChange={(e) => setWeeklyLimit(Math.max(0, parseInt(e.target.value) || 0))}
              className="border rounded-lg px-3 py-2 w-20 text-center" />
            <span className="text-sm text-gray-700">회</span>
            <button onClick={saveLimit} disabled={savingLimit}
              className="bg-amber-600 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
              {savingLimit ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      )}
      <div className="bg-white border rounded-xl p-4 mb-5 shadow-sm">
        <h3 className="font-semibold mb-1">새 {noun} {category === 'studyroom' ? '방' : '종류'} 만들기</h3>
        <p className="text-xs text-gray-400 mb-3">예: {example}. 만든 뒤 아래에서 예약 가능한 날짜/시간칸과 정원(자리 수)을 등록하세요. 학생은 열어둔 칸만 신청할 수 있습니다.{category === 'studyroom' ? ' (스터디룸은 학생이 취소 가능)' : ''}</p>
        <div className="flex flex-col gap-2 max-w-xl">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`${noun} 이름 (예: ${example.split(',')[0]})`} className="border rounded-lg px-3 py-2" />
          <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="설명 (선택)" className="border rounded-lg px-3 py-2" />
          <button onClick={create} disabled={busy} className="self-start bg-blue-600 text-white rounded-lg px-6 py-2 font-semibold">{noun} 만들기</button>
        </div>
      </div>

      {programs.length === 0 && <div className="text-gray-400 text-sm px-1">아직 만든 {noun}이(가) 없습니다.</div>}
      {programs.map((p) => <ProgramCard key={p.id} program={p} noun={noun} onChange={load} />)}
    </div>
  );
}

const HHMM = (h: number, m = 0) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

function ProgramCard({ program, noun, onChange }: { program: Program; noun: string; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [busy, setBusy] = useState(false);
  // 시간칸 추가 폼
  const [mode, setMode] = useState<'single' | 'recurring'>('recurring');
  const [slotDate, setSlotDate] = useState(todayKST());
  const [weekdays, setWeekdays] = useState<number[]>([4]); // 기본 목요일
  const [fromDate, setFromDate] = useState(todayKST());
  const [toDate, setToDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 28); return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' }); });
  const [ranges, setRanges] = useState<{ start: string; end: string }[]>([{ start: HHMM(18), end: HHMM(19) }]);
  const [capacity, setCapacity] = useState('1');
  // 명단
  const [rosterSlot, setRosterSlot] = useState<string | null>(null);
  const [roster, setRoster] = useState<Booking[]>([]);
  // 프로그램 전체 신청자 명단 (시간칸별로 묶어서 한눈에)
  const [showRoster, setShowRoster] = useState(false);
  const [progRoster, setProgRoster] = useState<{ slot_date: string; weekday_label: string; start_time: string; end_time: string; capacity: number; names: string[] }[]>([]);

  const loadSlots = useCallback(async () => {
    const res = await fetch(capi(`/programs/${program.id}/slots`), { headers: authHeader() });
    if (res.ok) setSlots((await res.json()).slots || []);
  }, [program.id]);
  useEffect(() => { if (open) loadSlots(); }, [open, loadSlots]);

  const loadProgRoster = useCallback(async () => {
    const res = await fetch(capi(`/programs/${program.id}/bookings`), { headers: authHeader() });
    if (res.ok) setProgRoster((await res.json()).slots || []);
  }, [program.id]);
  const toggleProgRoster = async () => {
    if (!showRoster) await loadProgRoster();
    setShowRoster((v) => !v);
  };

  const toggleWd = (w: number) => setWeekdays((prev) => prev.includes(w) ? prev.filter((x) => x !== w) : [...prev, w].sort());
  const setRange = (i: number, patch: Partial<{ start: string; end: string }>) => setRanges((v) => v.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRange = () => setRanges((v) => [...v, { start: HHMM(19), end: HHMM(20) }]);
  const rmRange = (i: number) => setRanges((v) => v.filter((_, idx) => idx !== i));

  const addSlots = async () => {
    const cap = parseInt(capacity, 10);
    if (!cap || cap < 1) { alert('정원은 1명 이상이어야 합니다'); return; }
    if (mode === 'recurring' && weekdays.length === 0) { alert('반복할 요일을 선택하세요'); return; }
    setBusy(true);
    try {
      const body: any = { mode, capacity: cap, time_ranges: ranges };
      if (mode === 'single') body.slot_date = slotDate;
      else { body.weekdays = weekdays; body.from_date = fromDate; body.to_date = toDate; }
      const res = await fetch(capi(`/programs/${program.id}/slots`), {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(body),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { alert(j.detail || '추가 실패'); return; }
      alert(j.message || '추가되었습니다');
      await loadSlots(); onChange();
    } finally { setBusy(false); }
  };

  const patchProgram = async (patch: any) => {
    await fetch(capi(`/programs/${program.id}`), { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(patch) });
    onChange();
  };
  const removeProgram = async () => {
    if (!window.confirm(`'${program.title}' ${noun}을(를) 삭제할까요?\n(등록된 시간칸과 학생 신청 내역도 함께 삭제됩니다)`)) return;
    await fetch(capi(`/programs/${program.id}`), { method: 'DELETE', headers: authHeader() });
    onChange();
  };
  const patchSlot = async (id: string, patch: any) => {
    await fetch(capi(`/slots/${id}`), { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(patch) });
    await loadSlots(); onChange();
  };
  const removeSlot = async (id: string) => {
    if (!window.confirm('이 시간칸을 삭제할까요? (신청 내역도 삭제됩니다)')) return;
    await fetch(capi(`/slots/${id}`), { method: 'DELETE', headers: authHeader() });
    await loadSlots(); onChange();
  };
  const viewRoster = async (id: string) => {
    if (rosterSlot === id) { setRosterSlot(null); return; }
    const res = await fetch(capi(`/slots/${id}/bookings`), { headers: authHeader() });
    if (res.ok) { setRoster((await res.json()).bookings || []); setRosterSlot(id); }
  };
  const cancelBooking = async (slotId: string, b: Booking) => {
    if (!window.confirm(`${b.name} 님의 신청을 취소할까요?`)) return;
    const res = await fetch(capi(`/slots/${slotId}/bookings/cancel`), {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ student_id: b.student_id }),
    });
    if (!res.ok) { alert((await res.json().catch(() => ({}))).detail || '취소 실패'); return; }
    const res2 = await fetch(capi(`/slots/${slotId}/bookings`), { headers: authHeader() });
    if (res2.ok) setRoster((await res2.json()).bookings || []);
    await loadSlots(); onChange();
  };

  return (
    <div className="bg-white border rounded-xl p-4 mb-3 shadow-sm">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="font-semibold text-lg">{program.title}</span>
          {program.status !== 'open' && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">숨김</span>}
          {program.description && <span className="ml-2 text-gray-400 text-sm">{program.description}</span>}
          <div className="text-xs text-gray-400 mt-0.5">시간칸 {program.slot_count}개 · 총 신청 {program.booking_count}명</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={toggleProgRoster} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${showRoster ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600 border border-indigo-300'}`}>
            🧑‍🤝‍🧑 신청자 명단 ({program.booking_count})
          </button>
          <button onClick={() => setOpen((v) => !v)} className="px-3 py-1.5 rounded-lg border text-sm">{open ? '닫기' : '시간칸 관리'}</button>
          <button onClick={() => downloadCsv(`/api/v1/owner/consulting/programs/${program.id}/export`, `${program.title}_전체명단.csv`)} className="px-3 py-1.5 rounded-lg border border-green-500 text-green-600 text-sm">전체 엑셀</button>
          <button onClick={() => patchProgram({ status: program.status === 'open' ? 'closed' : 'open' })} className="px-3 py-1.5 rounded-lg border text-sm">{program.status === 'open' ? '숨기기' : '보이기'}</button>
          <button onClick={removeProgram} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-500 text-sm">삭제</button>
        </div>
      </div>

      {/* 신청자 명단 (시간칸별) — 누가 신청했는지 한눈에 */}
      {showRoster && (
        <div className="mt-3 border-t pt-3">
          {progRoster.filter((s) => s.names.length > 0).length === 0 ? (
            <div className="text-gray-400 text-sm">아직 신청자가 없습니다.</div>
          ) : (
            <div className="space-y-2">
              {progRoster.filter((s) => s.names.length > 0).map((s, i) => (
                <div key={i} className="flex items-start gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-700 shrink-0">
                    {s.slot_date} ({s.weekday_label}) {s.start_time}~{s.end_time}
                    <span className="text-gray-400 font-normal"> · {s.names.length}/{s.capacity}명</span>
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {s.names.map((n, j) => (
                      <span key={j} className="px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded-full text-sm">{n}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="mt-4 border-t pt-4">
          {/* 시간칸 추가 */}
          <div className="bg-gray-50 rounded-xl p-3 mb-4">
            <div className="flex gap-2 mb-3">
              {([['recurring', '매주 반복'], ['single', '단건']] as const).map(([k, label]) => (
                <button key={k} onClick={() => setMode(k)} className={`px-3 py-1 rounded-full text-sm font-semibold ${mode === k ? 'bg-blue-600 text-white' : 'bg-white border text-gray-500'}`}>{label}</button>
              ))}
            </div>
            {mode === 'recurring' ? (
              <div className="space-y-2">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-sm text-gray-500 mr-1">요일</span>
                  {WD.map((w, i) => (
                    <button key={i} onClick={() => toggleWd(i)} className={`w-8 h-8 rounded-full text-sm font-semibold ${weekdays.includes(i) ? 'bg-blue-600 text-white' : 'bg-white border text-gray-500'}`}>{w}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="text-gray-500">기간</span>
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border rounded-lg px-2 py-1" />
                  <span className="text-gray-400">~</span>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border rounded-lg px-2 py-1" />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">날짜</span>
                <input type="date" value={slotDate} onChange={(e) => setSlotDate(e.target.value)} className="border rounded-lg px-2 py-1" />
              </div>
            )}
            <div className="mt-3">
              <div className="text-sm text-gray-500 mb-1">시간대 (여러 개 추가 가능)</div>
              {ranges.map((r, i) => (
                <div key={i} className="flex items-center gap-2 mb-1">
                  <input type="time" value={r.start} onChange={(e) => setRange(i, { start: e.target.value })} className="border rounded-lg px-2 py-1 text-sm" />
                  <span className="text-gray-400">~</span>
                  <input type="time" value={r.end} onChange={(e) => setRange(i, { end: e.target.value })} className="border rounded-lg px-2 py-1 text-sm" />
                  {ranges.length > 1 && <button onClick={() => rmRange(i)} className="px-2 py-1 rounded-lg border border-red-300 text-red-500 text-xs">삭제</button>}
                </div>
              ))}
              <button onClick={addRange} className="px-3 py-1 rounded-lg border border-blue-400 text-blue-600 text-xs mt-1">+ 시간대 추가</button>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500">정원</span>
              <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} className="border rounded-lg px-2 py-1 w-20 text-sm" />
              <span className="text-sm text-gray-400">명</span>
              <button onClick={addSlots} disabled={busy} className="ml-auto bg-blue-600 text-white rounded-lg px-5 py-1.5 text-sm font-semibold">시간칸 추가</button>
            </div>
          </div>

          {/* 시간칸 목록 */}
          {slots.length === 0 ? <div className="text-gray-400 text-sm">등록된 시간칸이 없습니다.</div> : (
            <div className="space-y-2">
              {slots.map((s) => (
                <div key={s.id} className={`border rounded-lg p-2.5 ${s.is_past ? 'opacity-50' : ''}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm">
                      <b>{s.slot_date}</b> ({s.weekday_label}) {s.start_time}~{s.end_time}
                      <span className="ml-2 text-gray-500">{s.booked_count}/{s.capacity}명</span>
                      {s.is_full && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-500">마감</span>}
                      {s.status !== 'open' && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">숨김</span>}
                      {s.is_past && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">지남</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => viewRoster(s.id)} className="px-2.5 py-1 rounded-lg border text-xs">{rosterSlot === s.id ? '명단 닫기' : `명단 (${s.booked_count})`}</button>
                      <button onClick={() => downloadCsv(`/api/v1/owner/consulting/slots/${s.id}/export`, `${program.title}_${s.slot_date}_명단.csv`)} className="px-2.5 py-1 rounded-lg border border-green-500 text-green-600 text-xs">엑셀</button>
                      <button onClick={() => patchSlot(s.id, { status: s.status === 'open' ? 'closed' : 'open' })} className="px-2.5 py-1 rounded-lg border text-xs">{s.status === 'open' ? '숨기기' : '보이기'}</button>
                      <button onClick={() => removeSlot(s.id)} className="px-2.5 py-1 rounded-lg border border-red-300 text-red-500 text-xs">삭제</button>
                    </div>
                  </div>
                  {rosterSlot === s.id && (
                    <div className="mt-2 pt-2 border-t">
                      {roster.length === 0 ? <div className="text-gray-400 text-xs">신청자가 없습니다</div> : (
                        <div className="flex flex-wrap gap-2">
                          {roster.map((b, i) => (
                            <span key={b.student_id} className="px-2.5 py-1 bg-indigo-50 border border-indigo-200 rounded-full text-sm inline-flex items-center gap-1">
                              {i + 1}. {b.name}
                              <button onClick={() => cancelBooking(s.id, b)} title="신청 취소" className="ml-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200 text-xs leading-none">✕</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===== 스터디룸 유료 이용 정산 (2026-07-21) =====
   앱에서 결제하지 않는다. 여기서 확인하고 매장에서 직접 수납. */
interface ChargeStudent { student_id: string; student_name: string; free_hours?: number; paid_hours: number; amount: number; count: number; }
interface ChargeItem { student_name: string; date: string; start_time: string; end_time: string; room: string; free_hours: number; paid_hours: number; hourly_price: number; amount: number; booked_by?: string; by_counter?: boolean; }

function StudyroomCharges() {
  const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' }); };
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayKST());
  const [students, setStudents] = useState<ChargeStudent[]>([]);
  const [items, setItems] = useState<ChargeItem[]>([]);
  const [totals, setTotals] = useState<{ free_hours?: number; paid_hours: number; amount: number; students: number; count: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  // 무료로만 쓴 예약까지 = 지난 이용 내역 보기 (2026-11-03, 마포대흥 문의)
  const [includeFree, setIncludeFree] = useState(false);
  const q = `start_date=${start}&end_date=${end}${includeFree ? '&include_free=true' : ''}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(capi(`/studyroom-charges?${q}`), { headers: authHeader() });
      if (!res.ok) { alert('정산 조회에 실패했습니다'); return; }
      const d = await res.json();
      setStudents(d.students || []); setItems(d.items || []); setTotals(d.totals || null);
    } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border rounded-xl p-4 mb-5 shadow-sm">
      <h3 className="font-semibold mb-1">{includeFree ? '📋 스터디룸 이용 내역' : '💰 유료 이용 정산'}</h3>
      <p className="text-xs text-gray-400 mb-3">{includeFree
        ? <>기간 안에 이용한 예약 전체입니다(<b>무료 이용 포함</b>). 지난 날짜도 고를 수 있습니다. (취소된 예약은 제외)</>
        : <>무료 시간을 넘겨 <b>부과된 금액</b>입니다. 앱 결제가 아니므로 이 명단을 보고 매장에서 받으세요. (취소된 예약은 제외)</>}</p>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" />
        <span className="text-gray-400">~</span>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" />
        <button onClick={load} disabled={loading} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
          {loading ? '조회 중...' : '조회'}
        </button>
        <button
          onClick={() => downloadCsv(`/api/v1/owner/consulting/studyroom-charges/export?${q}`, `스터디룸_${includeFree ? '이용내역' : '유료정산'}_${start}~${end}.csv`)}
          className="px-3 py-1.5 rounded-lg border border-green-500 text-green-600 text-sm">엑셀(CSV)</button>
        <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none">
          <input type="checkbox" checked={includeFree} onChange={(e) => setIncludeFree(e.target.checked)} />
          무료 이용도 보기
        </label>
      </div>

      {totals && (
        <div className="flex flex-wrap gap-3 mb-3 text-sm">
          <span className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-800 font-semibold">합계 {totals.amount.toLocaleString()}원</span>
          {includeFree && <span className="px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600">무료 {totals.free_hours ?? 0}시간</span>}
          <span className="px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600">유료 {totals.paid_hours}시간</span>
          <span className="px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600">학생 {totals.students}명 · {totals.count}건</span>
        </div>
      )}

      {students.length === 0 ? (
        <div className="text-sm text-gray-400 py-6 text-center">{includeFree ? '이 기간에 스터디룸 이용이 없습니다.' : '이 기간에 유료 이용이 없습니다.'}</div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs">
              <tr>
                <th className="text-left px-3 py-2 font-medium">학생</th>
                {includeFree && <th className="text-right px-3 py-2 font-medium">무료 시간</th>}
                <th className="text-right px-3 py-2 font-medium">유료 시간</th>
                <th className="text-right px-3 py-2 font-medium">건수</th>
                <th className="text-right px-3 py-2 font-medium">받을 금액</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.student_id} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium text-gray-800">{s.student_name}</td>
                  {includeFree && <td className="px-3 py-2 text-right text-gray-600">{s.free_hours ?? 0}시간</td>}
                  <td className="px-3 py-2 text-right text-gray-600">{s.paid_hours}시간</td>
                  <td className="px-3 py-2 text-right text-gray-400">{s.count}</td>
                  <td className="px-3 py-2 text-right font-semibold text-amber-700">{s.amount.toLocaleString()}원</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={() => setShowDetail((v) => !v)} className="mt-2 text-xs text-blue-600 hover:underline">
            {showDetail ? '건별 내역 접기' : `건별 내역 보기 (${items.length}건)`}
          </button>
          {showDetail && (
            <div className="mt-2 max-h-72 overflow-y-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">학생</th>
                    <th className="text-left px-2 py-1.5">날짜</th>
                    <th className="text-left px-2 py-1.5">시간</th>
                    <th className="text-left px-2 py-1.5">스터디룸</th>
                    <th className="text-left px-2 py-1.5">접수</th>
                    {includeFree && <th className="text-right px-2 py-1.5">무료</th>}
                    <th className="text-right px-2 py-1.5">유료</th>
                    <th className="text-right px-2 py-1.5">금액</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-2 py-1.5">{it.student_name}</td>
                      <td className="px-2 py-1.5 text-gray-500">{it.date}</td>
                      <td className="px-2 py-1.5 text-gray-500">{it.start_time}~{it.end_time}</td>
                      <td className="px-2 py-1.5 text-gray-500">{it.room}</td>
                      <td className={`px-2 py-1.5 ${it.by_counter ? 'text-emerald-700 font-semibold' : 'text-gray-400'}`}>{it.booked_by || '학생 앱'}</td>
                      {includeFree && <td className="px-2 py-1.5 text-right text-gray-500">{it.free_hours}h</td>}
                      <td className="px-2 py-1.5 text-right">{it.paid_hours}h</td>
                      <td className="px-2 py-1.5 text-right text-amber-700">{it.amount.toLocaleString()}원</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ============ 스터디룸(범위 예약) 탭 — 2026-07-14 ============
   원장은 방마다 "설정"만 하면 되고(운영시간·이용길이·정원), 학생은 앱에서
   날짜→길이→시작시간으로 예약. 시간칸 수동 생성 불필요. */
// 예약 단위는 1시간 고정 — 원장은 "최대 몇 시간까지" 만 정한다(학생은 1~최대 중 선택)
const DURATION_CHOICES = [1, 2, 3, 4, 5, 6];
const hoursUpTo = (max: number) => Array.from({ length: Math.max(1, max) }, (_, i) => i + 1);
const maxOf = (opts?: number[]) => (opts && opts.length ? Math.max(...opts) : 2);

type CancelMode = 'none' | 'hours' | 'day_before';
interface RangeBooking { booking_id: string; date: string; weekday_label: string; start_time: string; end_time: string; student_name: string; booked_by?: string; by_counter?: boolean; }

function StudyroomRangeTab() {
  const [programs, setPrograms] = useState<Program[]>([]);
  // 이용 정책 (2026-07-21: 횟수 → 시간 기준 + 유료 추가예약)
  const [freeHours, setFreeHours] = useState<number>(2);
  const [freeUnlimited, setFreeUnlimited] = useState(false);
  const [paidEnabled, setPaidEnabled] = useState(false);
  const [paidPrice, setPaidPrice] = useState<number>(5000);
  const [cancelDeadline, setCancelDeadline] = useState<number>(0);
  // 예약·취소 마감 방식 (2026-09-24): none / hours(시작 N시간 전 취소만) / day_before(전날 HH:MM 예약·취소 함께 마감)
  const [cancelMode, setCancelMode] = useState<CancelMode>('none');
  const [cutoffTime, setCutoffTime] = useState('22:00');
  const [cutoffOffset, setCutoffOffset] = useState<number>(1);
  const [savingLimit, setSavingLimit] = useState(false);
  const [busy, setBusy] = useState(false);
  // 새 방 만들기 폼
  const [title, setTitle] = useState('');
  const [openT, setOpenT] = useState('08:00');
  const [closeT, setCloseT] = useState('22:00');
  const [maxDur, setMaxDur] = useState<number>(2);   // 최대 이용 시간(1시간 단위)
  const [cap, setCap] = useState('1');
  const [wds, setWds] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);

  const load = useCallback(async () => {
    const res = await fetch(capi('/programs?category=studyroom'), { headers: authHeader() });
    if (res.ok) setPrograms((await res.json()).programs || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const res = await fetch(capi('/studyroom-settings'), { headers: authHeader() });
      if (!res.ok) return;
      const d = await res.json();
      const fh = d.free_weekly_hours;
      setFreeUnlimited(fh === null || fh === undefined);
      setFreeHours(fh ?? 2);
      setPaidEnabled(!!d.paid_enabled);
      setPaidPrice(d.paid_hourly_price ?? 5000);
      setCancelDeadline(d.cancel_deadline_hours ?? 0);
      setCancelMode((d.cancel_mode as CancelMode) || ((d.cancel_deadline_hours ?? 0) > 0 ? 'hours' : 'none'));
      setCutoffTime(d.cutoff_time || '22:00');
      setCutoffOffset(d.cutoff_day_offset || 1);
    })();
  }, []);

  const saveLimit = async () => {
    // '시작 N시간 전'에 0 을 넣으면 서버는 제한 없음으로 동작한다 → 화면과 실제가 어긋나지 않게 막는다
    if (cancelMode === 'hours' && cancelDeadline < 1) { alert('취소 마감 시간을 1시간 이상으로 넣어 주세요. 제한을 두지 않으려면 "제한 없음"을 고르세요.'); return; }
    setSavingLimit(true);
    try {
      const res = await fetch(capi('/studyroom-settings'), {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          free_weekly_hours: freeUnlimited ? null : freeHours,
          paid_enabled: paidEnabled,
          paid_hourly_price: paidPrice,
          cancel_deadline_hours: cancelDeadline,
          cancel_mode: cancelMode,
          cutoff_time: cutoffTime,
          cutoff_day_offset: cutoffOffset,
        }),
      });
      alert(res.ok ? '저장되었습니다' : ((await res.json().catch(() => ({}))).detail || '저장 실패'));
    } finally { setSavingLimit(false); }
  };

  const toggleWd = (w: number) => setWds((v) => v.includes(w) ? v.filter((x) => x !== w) : [...v, w].sort());

  const create = async () => {
    if (!title.trim()) { alert('스터디룸 이름을 입력하세요'); return; }
    if (wds.length === 0) { alert('운영 요일을 1개 이상 선택하세요'); return; }
    if (openT >= closeT) { alert('종료 시간이 시작 시간보다 늦어야 합니다'); return; }
    const c = parseInt(cap, 10); if (!c || c < 1) { alert('정원은 1 이상이어야 합니다'); return; }
    setBusy(true);
    try {
      const res = await fetch(capi('/programs'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          title: title.trim(), category: 'studyroom',
          open_time: openT, close_time: closeT, slot_minutes: 30,
          duration_options: hoursUpTo(maxDur), capacity: c, open_weekdays: wds, advance_days: 14,
        }),
      });
      if (!res.ok) { alert((await res.json().catch(() => ({}))).detail || '생성 실패'); return; }
      setTitle(''); setMaxDur(2); setCap('1'); await load();
    } finally { setBusy(false); }
  };

  return (
    <div>
      {/* 이용 정책 (무료 시간 + 유료 추가예약) */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
        <h3 className="font-semibold mb-1">🪑 스터디룸 이용 정책</h3>
        <p className="text-xs text-gray-500 mb-3">
          무료로 쓸 수 있는 <b>주간 시간</b>을 정하고, 그 이상은 <b>시간당 요금</b>을 받아 추가 예약을 허용할 수 있습니다.
          (모든 방 합산 · 월~일 기준 · 예약은 1시간 단위)
        </p>

        {/* 무료 시간 */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-sm text-gray-700 w-24">주간 무료</span>
          <input type="number" min={0} max={168} value={freeHours} disabled={freeUnlimited}
            onChange={(e) => setFreeHours(Math.max(0, parseInt(e.target.value) || 0))}
            className="border rounded-lg px-3 py-2 w-20 text-center disabled:bg-gray-100 disabled:text-gray-400" />
          <span className="text-sm text-gray-700">시간</span>
          <label className="flex items-center gap-1 text-xs text-gray-600 ml-2 cursor-pointer select-none">
            <input type="checkbox" checked={freeUnlimited} onChange={(e) => setFreeUnlimited(e.target.checked)} />
            무제한 무료
          </label>
        </div>

        {/* 유료 추가 */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-sm text-gray-700 w-24">유료 추가예약</span>
          <label className="flex items-center gap-1 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={paidEnabled} onChange={(e) => setPaidEnabled(e.target.checked)} />
            허용
          </label>
          <input type="number" min={0} step={500} value={paidPrice} disabled={!paidEnabled}
            onChange={(e) => setPaidPrice(Math.max(0, parseInt(e.target.value) || 0))}
            className="border rounded-lg px-3 py-2 w-28 text-center ml-2 disabled:bg-gray-100 disabled:text-gray-400" />
          <span className="text-sm text-gray-700">원 / 시간</span>
        </div>

        {/* 예약·취소 마감 (2026-09-24) */}
        <div className="flex items-start gap-2 flex-wrap mb-3">
          <span className="text-sm text-gray-700 w-24 pt-1.5">예약·취소 마감</span>
          <div className="flex flex-col gap-1.5 text-sm">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input type="radio" name="sr-cancel-mode" checked={cancelMode === 'none'} onChange={() => setCancelMode('none')} />
              제한 없음 <span className="text-gray-400 text-xs">(언제든 예약·취소)</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer select-none flex-wrap">
              <input type="radio" name="sr-cancel-mode" checked={cancelMode === 'hours'} onChange={() => setCancelMode('hours')} />
              시작
              <input type="number" min={1} value={cancelDeadline} disabled={cancelMode !== 'hours'}
                onChange={(e) => setCancelDeadline(Math.max(0, parseInt(e.target.value) || 0))}
                className="border rounded-lg px-2 py-1 w-16 text-center disabled:bg-gray-100 disabled:text-gray-400" />
              시간 전까지만 취소 <span className="text-gray-400 text-xs">(예약은 제한 없음)</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer select-none flex-wrap">
              <input type="radio" name="sr-cancel-mode" checked={cancelMode === 'day_before'} onChange={() => setCancelMode('day_before')} />
              <select value={cutoffOffset} disabled={cancelMode !== 'day_before'}
                onChange={(e) => setCutoffOffset(parseInt(e.target.value, 10))}
                className="border rounded-lg px-2 py-1 disabled:bg-gray-100 disabled:text-gray-400">
                <option value={1}>전날</option>
                <option value={2}>2일 전</option>
                <option value={3}>3일 전</option>
              </select>
              <input type="time" value={cutoffTime} disabled={cancelMode !== 'day_before'} step={1800}
                onChange={(e) => setCutoffTime(e.target.value || '22:00')}
                className="border rounded-lg px-2 py-1 disabled:bg-gray-100 disabled:text-gray-400" />
              에 예약·취소 모두 마감
            </label>
            {cancelMode === 'day_before' && (
              <div className="text-xs text-amber-800 bg-amber-100/70 rounded-lg px-2.5 py-1.5 mt-0.5">
                마감이 지나면 학생 앱에서 그 날짜는 예약도 취소도 되지 않습니다.
                당일 이용은 카운터에서 받아 아래 방의 <b>「+ 예약 추가」</b>로 넣어 주세요. (유료 정산·주간 무료시간에 똑같이 잡힙니다)
              </div>
            )}
          </div>
        </div>

        <div className="text-xs text-gray-600 bg-white/70 rounded-lg px-3 py-2 mb-3">
          {freeUnlimited
            ? '지금 설정: 무제한 무료 (요금 부과 없음)'
            : paidEnabled
              ? `지금 설정: 주 ${freeHours}시간 무료, 초과분은 시간당 ${paidPrice.toLocaleString()}원 부과`
              : `지금 설정: 주 ${freeHours}시간 무료, 초과하면 예약 불가 (유료 미허용)`}
          <div className="mt-0.5">
            {cancelMode === 'none' ? '예약·취소: 제한 없음'
              : cancelMode === 'hours' ? `예약: 제한 없음 · 취소: 시작 ${cancelDeadline}시간 전까지`
                : `예약·취소: 이용일 ${cutoffOffset === 1 ? '전날' : `${cutoffOffset}일 전`} ${cutoffTime} 마감 · 이후는 카운터 접수`}
          </div>
          <div className="text-gray-400 mt-0.5">※ 실제 결제는 앱에서 하지 않습니다. 부과 금액은 아래 정산에서 확인하고 매장에서 수납하세요.</div>
        </div>

        <button onClick={saveLimit} disabled={savingLimit} className="bg-amber-600 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
          {savingLimit ? '저장 중...' : '정책 저장'}
        </button>
      </div>

      {/* 유료 이용 정산 */}
      <StudyroomCharges />

      {/* 새 방 만들기 (설정 폼) */}
      <div className="bg-white border rounded-xl p-4 mb-5 shadow-sm">
        <h3 className="font-semibold mb-1">새 스터디룸 방 만들기</h3>
        <p className="text-xs text-gray-400 mb-3">운영 시간과 이용 길이만 정하면, 학생은 앱에서 날짜·시작시간을 골라 예약합니다. (시간칸을 일일이 만들 필요 없음 · 30분 단위)</p>
        <div className="flex flex-col gap-3 max-w-xl">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="스터디룸 이름 (예: 세미나실, 1인 부스)" className="border rounded-lg px-3 py-2" />
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="text-gray-500 w-16">운영 시간</span>
            <input type="time" value={openT} onChange={(e) => setOpenT(e.target.value)} className="border rounded-lg px-2 py-1" step={1800} />
            <span className="text-gray-400">~</span>
            <input type="time" value={closeT} onChange={(e) => setCloseT(e.target.value)} className="border rounded-lg px-2 py-1" step={1800} />
          </div>
          <div className="flex items-start gap-2 text-sm flex-wrap">
            <span className="text-gray-500 w-16 pt-1.5">최대 이용</span>
            <div className="flex gap-1.5 flex-wrap">
              {DURATION_CHOICES.map((h) => (
                <button key={h} onClick={() => setMaxDur(h)} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${maxDur === h ? 'bg-blue-600 text-white' : 'bg-white border text-gray-500'}`}>{h}시간</button>
              ))}
              <span className="text-xs text-gray-400 self-center ml-1">학생은 1시간 단위로 1~{maxDur}시간 중 선택</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm flex-wrap">
            <span className="text-gray-500 w-16 pt-1.5">운영 요일</span>
            <div className="flex gap-1 flex-wrap">
              {WD.map((w, i) => (
                <button key={i} onClick={() => toggleWd(i)} className={`w-8 h-8 rounded-full text-sm font-semibold ${wds.includes(i) ? 'bg-blue-600 text-white' : 'bg-white border text-gray-500'}`}>{w}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 w-16">정원</span>
            <input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} className="border rounded-lg px-2 py-1 w-20" />
            <span className="text-gray-400">명 동시 이용</span>
          </div>
          <button onClick={create} disabled={busy} className="self-start bg-blue-600 text-white rounded-lg px-6 py-2 font-semibold">스터디룸 만들기</button>
        </div>
      </div>

      {programs.length === 0 && <div className="text-gray-400 text-sm px-1">아직 만든 스터디룸이 없습니다.</div>}
      {programs.map((p) => <StudyroomRoomCard key={p.id} program={p} onChange={load} />)}
    </div>
  );
}

function StudyroomRoomCard({ program, onChange }: { program: Program; onChange: () => void }) {
  const [edit, setEdit] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [bookings, setBookings] = useState<RangeBooking[]>([]);
  // 편집 상태
  const [openT, setOpenT] = useState(program.open_time || '08:00');
  const [closeT, setCloseT] = useState(program.close_time || '22:00');
  const [maxDur, setMaxDur] = useState<number>(maxOf(program.duration_options));
  const [cap, setCap] = useState(String(program.capacity || 1));
  const [wds, setWds] = useState<number[]>(program.open_weekdays || [0, 1, 2, 3, 4, 5, 6]);

  const loadBookings = useCallback(async () => {
    const res = await fetch(capi(`/programs/${program.id}/range-bookings`), { headers: authHeader() });
    if (res.ok) setBookings((await res.json()).bookings || []);
  }, [program.id]);
  const toggleRoster = async () => { if (!showRoster) await loadBookings(); setShowRoster((v) => !v); };

  const toggleWd = (w: number) => setWds((v) => v.includes(w) ? v.filter((x) => x !== w) : [...v, w].sort());

  const saveSettings = async () => {
    if (wds.length === 0) { alert('운영 요일을 1개 이상 선택하세요'); return; }
    if (openT >= closeT) { alert('종료 시간이 시작 시간보다 늦어야 합니다'); return; }
    const res = await fetch(capi(`/programs/${program.id}`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ open_time: openT, close_time: closeT, duration_options: hoursUpTo(maxDur), capacity: parseInt(cap, 10) || 1, open_weekdays: wds }),
    });
    if (!res.ok) { alert((await res.json().catch(() => ({}))).detail || '저장 실패'); return; }
    setEdit(false); onChange();
  };
  const patchStatus = async () => {
    await fetch(capi(`/programs/${program.id}`), { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ status: program.status === 'open' ? 'closed' : 'open' }) });
    onChange();
  };
  const remove = async () => {
    if (!window.confirm(`'${program.title}' 스터디룸을 삭제할까요?\n(예약 내역도 함께 삭제됩니다)`)) return;
    await fetch(capi(`/programs/${program.id}`), { method: 'DELETE', headers: authHeader() });
    onChange();
  };
  const cancelBooking = async (b: RangeBooking) => {
    if (!window.confirm(`${b.student_name} 님의 ${b.date} ${b.start_time} 예약을 취소할까요?`)) return;
    await fetch(capi(`/bookings/${b.booking_id}/cancel`), { method: 'POST', headers: authHeader() });
    await loadBookings(); onChange();
  };

  const wdText = (arr?: number[]) => (arr && arr.length === 7) ? '매일' : (arr || []).map((i) => WD[i]).join('·');
  // 날짜별 그룹
  const byDate: Record<string, RangeBooking[]> = {};
  bookings.forEach((b) => { (byDate[b.date] = byDate[b.date] || []).push(b); });

  return (
    <div className="bg-white border rounded-xl p-4 mb-3 shadow-sm">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="font-semibold text-lg">{program.title}</span>
          {program.status !== 'open' && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">숨김</span>}
          <div className="text-xs text-gray-500 mt-0.5">
            {program.open_time}~{program.close_time} · {wdText(program.open_weekdays)} · 이용 {(program.duration_options || []).map((h) => `${h}시간`).join('/')} · 정원 {program.capacity}명 · 30분 단위
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowAdd((v) => !v)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${showAdd ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 border border-emerald-300'}`}>+ 예약 추가</button>
          <button onClick={toggleRoster} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${showRoster ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600 border border-indigo-300'}`}>🧑‍🤝‍🧑 예약 명단</button>
          <button onClick={() => setEdit((v) => !v)} className="px-3 py-1.5 rounded-lg border text-sm">{edit ? '닫기' : '설정 수정'}</button>
          <button onClick={patchStatus} className="px-3 py-1.5 rounded-lg border text-sm">{program.status === 'open' ? '숨기기' : '보이기'}</button>
          <button onClick={remove} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-500 text-sm">삭제</button>
        </div>
      </div>

      {edit && (
        <div className="mt-3 border-t pt-3 bg-gray-50 rounded-xl p-3 space-y-2 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-500 w-16">운영 시간</span>
            <input type="time" value={openT} onChange={(e) => setOpenT(e.target.value)} className="border rounded-lg px-2 py-1" step={1800} />
            <span className="text-gray-400">~</span>
            <input type="time" value={closeT} onChange={(e) => setCloseT(e.target.value)} className="border rounded-lg px-2 py-1" step={1800} />
          </div>
          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-gray-500 w-16 pt-1.5">최대 이용</span>
            <div className="flex gap-1.5 flex-wrap">
              {DURATION_CHOICES.map((h) => (
                <button key={h} onClick={() => setMaxDur(h)} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${maxDur === h ? 'bg-blue-600 text-white' : 'bg-white border text-gray-500'}`}>{h}시간</button>
              ))}
              <span className="text-xs text-gray-400 self-center ml-1">학생은 1시간 단위로 1~{maxDur}시간 중 선택</span>
            </div>
          </div>
          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-gray-500 w-16 pt-1.5">운영 요일</span>
            <div className="flex gap-1 flex-wrap">
              {WD.map((w, i) => (
                <button key={i} onClick={() => toggleWd(i)} className={`w-8 h-8 rounded-full text-sm font-semibold ${wds.includes(i) ? 'bg-blue-600 text-white' : 'bg-white border text-gray-500'}`}>{w}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 w-16">정원</span>
            <input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} className="border rounded-lg px-2 py-1 w-20" />
            <span className="text-gray-400">명</span>
            <button onClick={saveSettings} className="ml-auto bg-blue-600 text-white rounded-lg px-5 py-1.5 font-semibold">설정 저장</button>
          </div>
        </div>
      )}

      {showAdd && (
        <StudyroomOwnerAddBooking program={program}
          onDone={async () => { if (showRoster) await loadBookings(); onChange(); }} />
      )}

      {showRoster && (
        <div className="mt-3 border-t pt-3">
          {bookings.length === 0 ? <div className="text-gray-400 text-sm">다가오는 예약이 없습니다.</div> : (
            <div className="space-y-2">
              {Object.keys(byDate).sort().map((d) => (
                <div key={d}>
                  <div className="text-sm font-semibold text-gray-700">{d} ({byDate[d][0].weekday_label})</div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {byDate[d].sort((a, b) => a.start_time.localeCompare(b.start_time)).map((b) => (
                      <span key={b.booking_id} title={b.booked_by || ''}
                        className={`px-2.5 py-1 rounded-full text-sm inline-flex items-center gap-1 border ${b.by_counter ? 'bg-emerald-50 border-emerald-300' : 'bg-indigo-50 border-indigo-200'}`}>
                        {b.start_time}~{b.end_time} {b.student_name}
                        {b.by_counter && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">카운터</span>}
                        <button onClick={() => cancelBooking(b)} title="예약 취소" className="ml-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200 text-xs leading-none">✕</button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* 원장 '예약 추가' — 카운터 접수 (2026-09-24)
   '전날 마감' 매장은 마감 뒤 당일 이용을 여기로 넣는다. 서버가 학생 예약과 같은 계산
   (정원·겹침·주간 무료/유료)을 하고 마감만 건너뛴다 → 명단·정산에 똑같이 잡힘. */
interface OwnerStudent { student_id: string; name: string; phone_tail: string; status: string; }
interface OwnerSlot { start: string; end: string; remaining: number; available: boolean; }

function StudyroomOwnerAddBooking({ program, onDone }: { program: Program; onDone: () => void }) {
  const durs = (program.duration_options && program.duration_options.length ? program.duration_options : [1]);
  const [students, setStudents] = useState<OwnerStudent[]>([]);
  const [q, setQ] = useState('');
  const [sid, setSid] = useState('');
  const [date, setDate] = useState(todayKST());
  const [dur, setDur] = useState<number>(durs[0]);
  const [slots, setSlots] = useState<OwnerSlot[]>([]);
  const [start, setStart] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      const res = await fetch(capi('/studyroom-students'), { headers: authHeader() });
      if (res.ok) setStudents((await res.json()).students || []);
    })();
  }, []);

  const loadSlots = useCallback(async () => {
    setStart('');
    const res = await fetch(capi(`/rooms/${program.id}/availability?date=${date}&duration=${dur}`), { headers: authHeader() });
    if (res.ok) setSlots((await res.json()).slots || []);
    else setSlots([]);
  }, [program.id, date, dur]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  const filtered = students.filter((s) => !q.trim() || s.name.includes(q.trim()) || s.phone_tail.includes(q.trim()));
  const picked = students.find((s) => s.student_id === sid);
  // 검색해서 한 명만 남으면 그 학생을 바로 고른다. 목록 상자는 하나 남은 항목을
  // 선택된 것처럼 칠해 보여 주지만 실제 선택(sid)은 비어 있어, 눌러도 바뀜 신호가
  // 오지 않아 '학생을 선택해 주세요'만 뜨던 문제(2026-11-05 대구범어 문의).
  useEffect(() => {
    if (filtered.length === 1 && filtered[0].student_id !== sid) setSid(filtered[0].student_id);
    else if (sid && filtered.length > 1 && !filtered.some((s) => s.student_id === sid)) setSid('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, students]);

  const submit = async () => {
    if (!sid) { setMsg({ ok: false, text: '학생을 선택해 주세요' }); return; }
    if (!start) { setMsg({ ok: false, text: '시작 시간을 선택해 주세요' }); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(capi(`/rooms/${program.id}/book`), {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ student_id: sid, date, start_time: start, duration: dur }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: d.detail || '예약 추가 실패' }); return; }
      setMsg({ ok: true, text: `${picked?.name || ''} 님 ${date} ${d.start_time}~${d.end_time} — ${d.message || '예약되었습니다'}` });
      await loadSlots(); onDone();
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t pt-3 bg-emerald-50/50 rounded-xl p-3 space-y-2.5 text-sm">
      <div className="text-xs text-gray-500">카운터에서 받은 이용을 학생 대신 넣습니다. 예약 마감과 관계없이 넣을 수 있고, 무료시간·유료 정산은 학생 예약과 똑같이 계산됩니다.</div>
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-gray-500 w-16 pt-1.5">학생</span>
        <div className="flex flex-col gap-1.5">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름 또는 전화 뒤 4자리" className="border rounded-lg px-2 py-1 w-56" />
          <select value={sid} onChange={(e) => setSid(e.target.value)} className="border rounded-lg px-2 py-1 w-56" size={Math.min(6, Math.max(2, filtered.length))}>
            {filtered.map((s) => (
              <option key={s.student_id} value={s.student_id} onClick={() => setSid(s.student_id)}>
                {s.name}{s.phone_tail ? ` (${s.phone_tail})` : ''}{s.status !== 'active' ? ' · 만료' : ''}
              </option>
            ))}
          </select>
          <span className={`text-sm ${picked ? 'text-emerald-700 font-semibold' : 'text-gray-400'}`}>{picked ? `선택됨: ${picked.name}` : '학생을 눌러 선택하세요'}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-gray-500 w-16">날짜</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border rounded-lg px-2 py-1" />
        <span className="text-gray-500 ml-3">이용</span>
        {durs.map((h) => (
          <button key={h} onClick={() => setDur(h)} className={`px-3 py-1 rounded-full text-sm font-semibold ${dur === h ? 'bg-blue-600 text-white' : 'bg-white border text-gray-500'}`}>{h}시간</button>
        ))}
      </div>
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-gray-500 w-16 pt-1">시작</span>
        <div className="flex gap-1.5 flex-wrap max-w-2xl">
          {slots.length === 0 && <span className="text-gray-400 text-xs pt-1">선택할 수 있는 시간이 없습니다</span>}
          {slots.map((sl) => (
            <button key={sl.start} disabled={!sl.available} onClick={() => setStart(sl.start)}
              title={sl.available ? `${sl.start}~${sl.end} · 남은 자리 ${sl.remaining}` : '정원 참'}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${start === sl.start ? 'bg-emerald-600 text-white border-emerald-600'
                : sl.available ? 'bg-white text-gray-700 border-gray-300' : 'bg-gray-100 text-gray-300 border-gray-200 line-through'}`}>
              {sl.start}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={submit} disabled={busy} className="bg-emerald-600 text-white rounded-lg px-5 py-1.5 font-semibold disabled:opacity-50">
          {busy ? '추가 중...' : '예약 추가'}
        </button>
        {picked && start && <span className="text-gray-600">{picked.name} · {date} {start} 부터 {dur}시간</span>}
      </div>
      {msg && <div className={`text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-50 text-red-600'}`}>{msg.text}</div>}
    </div>
  );
}
